import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip as RechartsTooltip, PieChart, Pie, Cell, Legend,
  LineChart, Line, CartesianGrid, LabelList,
} from 'recharts';
import {
  BarChart3, TrendingUp, Smile, MessageSquare, AlertTriangle, Loader2, Inbox, Users2, Info, History,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LoadErrorCard from '../components/LoadErrorCard';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';

const SEM_COLORS = { promotores: '#16a34a', pasivos: '#f59e0b', detractores: '#dc2626' };

const KpiCard = ({ label, value, sub, icon: Icon, color, testid, info }) => (
  <Card className="border-slate-200" data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
        <div className="flex items-center gap-1.5">
          {info && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="text-slate-300 hover:text-slate-500 transition-colors" data-testid={`${testid}-info`} aria-label="Cómo se calcula">
                  <Info className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="bg-white text-slate-700 border border-slate-200 shadow-lg max-w-[270px] p-3">
                {info}
              </TooltipContent>
            </Tooltip>
          )}
          {Icon && <Icon className="w-4 h-4" style={{ color: color || '#94a3b8' }} />}
        </div>
      </div>
      <p className="text-2xl font-bold mt-1" style={{ color: color || '#0f172a' }}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const Dot = ({ color }) => <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ backgroundColor: color }} />;

const npsColor = (n) => (n === null || n === undefined ? '#94a3b8' : n >= 50 ? '#16a34a' : n >= 0 ? '#f59e0b' : '#dc2626');

const CXAnalyticsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [data, setData] = useState(null);
  const [filtros, setFiltros] = useState({ proyectos: [], etapas: [], puntos: [] });
  const [scope, setScope] = useState('corporativo');
  const [codigoProyecto, setCodigoProyecto] = useState('');
  const [codigoEtapa, setCodigoEtapa] = useState('');
  const [puntoContacto, setPuntoContacto] = useState('');

  useAbortableLoad((signal) => {
    api.get('/experiencia/analytics/filtros', { signal }).then((r) => setFiltros(r.data)).catch(() => {});
  }, [api]);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = { scope };
      if (scope === 'proyecto') params.codigo_proyecto = codigoProyecto;
      if (scope === 'etapa') { params.codigo_proyecto = codigoProyecto; params.codigo_etapa = codigoEtapa; }
      if (puntoContacto) params.codigo_punto_contacto = puntoContacto;
      const res = await api.get('/experiencia/analytics', { params, signal });
      setData(res.data);
    } catch (e) {
      if (isCanceled(e)) return;
      setData(null);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [api, scope, codigoProyecto, codigoEtapa, puntoContacto]);

  useAbortableLoad((signal) => {
    if (scope === 'proyecto' && !codigoProyecto) return;
    if (scope === 'etapa' && !(codigoProyecto && codigoEtapa)) return;
    load(signal);
  }, [load, scope, codigoProyecto, codigoEtapa, puntoContacto]);

  const etapasDelProyecto = filtros.etapas.filter((e) => !codigoProyecto || e.codigo_proyecto === codigoProyecto);
  const puntoNombre = (filtros.puntos || []).find((p) => p.codigo === puntoContacto)?.nombre || puntoContacto;

  const dist = data?.distribucion || { promotores: 0, pasivos: 0, detractores: 0 };
  const distData = [
    { name: 'Promotores', value: dist.promotores, key: 'promotores' },
    { name: 'Pasivos', value: dist.pasivos, key: 'pasivos' },
    { name: 'Detractores', value: dist.detractores, key: 'detractores' },
  ];
  const sinDatos = !data || data.total_respuestas === 0;

  return (
    <div className="flex flex-col gap-5 animate-fadeIn" data-testid="cx-analytics-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6" /> CX Analytics
          </h1>
          <p className="text-slate-500 text-sm mt-1">Análisis de NPS y CSAT a partir de las encuestas recibidas.</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => navigate('/admin/experiencia/encuestas-historial')}
          data-testid="historial-encuestas-link"
        >
          <History className="w-4 h-4 mr-2" /> Historial de encuestas
        </Button>
      </div>

      {/* Filtros */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Segmentación</label>
            <Select value={scope} onValueChange={(v) => { setScope(v); if (v === 'corporativo') { setCodigoProyecto(''); setCodigoEtapa(''); } }}>
              <SelectTrigger className="w-full sm:w-52" data-testid="scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="corporativo">Corporativo (todos)</SelectItem>
                <SelectItem value="proyecto">Por Proyecto</SelectItem>
                <SelectItem value="etapa">Por Proyecto + Etapa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(scope === 'proyecto' || scope === 'etapa') && (
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Proyecto</label>
              <Select value={codigoProyecto} onValueChange={(v) => { setCodigoProyecto(v); setCodigoEtapa(''); }}>
                <SelectTrigger className="w-full sm:w-56" data-testid="proyecto-select"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                <SelectContent>
                  {filtros.proyectos.map((p) => <SelectItem key={p.codigo_proyecto} value={p.codigo_proyecto}>{p.nombre} ({p.codigo_proyecto})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {scope === 'etapa' && (
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Etapa</label>
              <Select value={codigoEtapa} onValueChange={setCodigoEtapa} disabled={!codigoProyecto}>
                <SelectTrigger className="w-full sm:w-52" data-testid="etapa-select"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                <SelectContent>
                  {etapasDelProyecto.map((e) => <SelectItem key={e.codigo} value={e.codigo}>{e.nombre} ({e.codigo})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Punto de contacto</label>
            <Select value={puntoContacto || '__todos__'} onValueChange={(v) => setPuntoContacto(v === '__todos__' ? '' : v)}>
              <SelectTrigger className="w-full sm:w-56" data-testid="punto-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__todos__" data-testid="punto-opt-todos">Todos</SelectItem>
                {(filtros.puntos || []).map((p) => <SelectItem key={p.codigo} value={p.codigo} data-testid={`punto-opt-${p.codigo}`}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : loadError ? (
        <LoadErrorCard onRetry={load} testId="cx-analytics-load-error" />
      ) : sinDatos ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center text-slate-500" data-testid="analytics-empty">
            <Inbox className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            No hay respuestas de encuestas para esta segmentación todavía.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPIs */}
          <TooltipProvider delayDuration={150}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            <KpiCard label="NPS" value={data.nps_score ?? '—'} sub={`${data.total_nps} respuestas`} icon={TrendingUp} color={npsColor(data.nps_score)} testid="kpi-nps"
              info={(
                <div className="space-y-1.5 text-left">
                  <p className="font-semibold text-slate-800">Cómo se calcula el NPS</p>
                  <p className="text-slate-500">NPS = % Promotores − % Detractores</p>
                  <div className="pt-0.5">
                    <p><Dot color="#16a34a" />Promotores (9–10): <b>{dist.promotores}</b></p>
                    <p><Dot color="#f59e0b" />Pasivos (7–8): <b>{dist.pasivos}</b></p>
                    <p><Dot color="#dc2626" />Detractores (0–6): <b>{dist.detractores}</b></p>
                    <p className="text-slate-500 pt-0.5">Total respuestas: <b>{data.total_nps}</b></p>
                  </div>
                  {data.total_nps > 0 && (
                    <p className="text-slate-500 pt-0.5">= ({dist.promotores} − {dist.detractores}) / {data.total_nps} × 100 = <b className="text-slate-800">{data.nps_score}</b></p>
                  )}
                  <p className="text-[10px] text-slate-400 pt-1">Cuenta cada respuesta (no cada cliente). Rango −100 a +100.</p>
                </div>
              )}
            />
            <KpiCard label="CSAT (0-5)" value={data.csat_promedio ?? '—'} sub={`${data.total_csat} respuestas`} icon={Smile} color="#3b82f6" testid="kpi-csat"
              info={(
                <div className="space-y-1.5 text-left">
                  <p className="font-semibold text-slate-800">Cómo se calcula el CSAT</p>
                  <p className="text-slate-500">CSAT = promedio de satisfacción, escala 0 a 5.</p>
                  <p className="text-slate-500">Suma de los puntajes CSAT ÷ nº de respuestas.</p>
                  <p className="pt-0.5">Respuestas con CSAT: <b>{data.total_csat}</b></p>
                  <p>Promedio actual: <b className="text-slate-800">{data.csat_promedio ?? '—'}</b></p>
                  <p className="text-[10px] text-slate-400 pt-1">Cuenta cada respuesta (no cada cliente).</p>
                </div>
              )}
            />
            <KpiCard label="Encuestas" value={data.total_respuestas} sub="total recibidas" icon={MessageSquare} color="#0ea5e9" testid="kpi-total" />
            <KpiCard label="Clientes críticos" value={data.clientes_criticos.length} sub="detractor + influyente" icon={AlertTriangle} color="#dc2626" testid="kpi-criticos" />
          </div>
          </TooltipProvider>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
            {/* Distribución */}
            <Card className="border-slate-200">
              <CardContent className="p-5 lg:p-6">
                <h2 className="text-sm font-semibold text-slate-700 mb-4">Distribución de clientes</h2>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={distData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                      {distData.map((d) => <Cell key={d.key} fill={SEM_COLORS[d.key]} />)}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Tendencia */}
            <Card className="border-slate-200">
              <CardContent className="p-5 lg:p-6">
                <h2 className="text-sm font-semibold text-slate-700 mb-4">Tendencia mensual</h2>
                {data.tendencia.length === 0 ? <p className="text-sm text-slate-400">Sin datos suficientes.</p> : (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={data.tendencia} margin={{ left: 0, right: 16, top: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="mes" fontSize={11} />
                      <YAxis yAxisId="nps" domain={[-100, 100]} fontSize={11} />
                      <YAxis yAxisId="csat" orientation="right" domain={[0, 5]} fontSize={11} />
                      <RechartsTooltip />
                      <Legend />
                      <Line yAxisId="nps" type="monotone" dataKey="nps" name="NPS" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
                      <Line yAxisId="csat" type="monotone" dataKey="csat" name="CSAT" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* NPS por punto de contacto */}
          <Card className="border-slate-200">
            <CardContent className="p-5 lg:p-6">
              <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                NPS por punto de contacto
                {puntoContacto && <Badge variant="secondary" data-testid="punto-filtro-badge">Filtrado: {puntoNombre}</Badge>}
              </h2>
              {data.por_punto.length === 0 ? <p className="text-sm text-slate-400">Sin datos.</p> : (
                <ResponsiveContainer width="100%" height={Math.max(220, data.por_punto.length * 44)}>
                  <BarChart data={data.por_punto} layout="vertical" margin={{ left: 16, right: 48, top: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" domain={[-100, 100]} fontSize={11} />
                    <YAxis type="category" dataKey="nombre" width={140} fontSize={11} />
                    <RechartsTooltip />
                    <Bar dataKey="nps" name="NPS" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                      {data.por_punto.map((p, i) => <Cell key={i} fill={npsColor(p.nps)} />)}
                      <LabelList dataKey="total" position="insideRight" offset={8} fontSize={11} fill="#ffffff" fontWeight={600} formatter={(v) => `n=${v}`} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* NPS por proyecto + Clientes críticos (lado a lado, alturas independientes) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
            {/* NPS por proyecto */}
            <Card className="border-slate-200" data-testid="tabla-nps-por-proyecto">
              <CardContent className="p-5 lg:p-6">
                <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-slate-400" /> NPS por proyecto</h2>
                {(!data.por_proyecto || data.por_proyecto.length === 0) ? (
                  <p className="text-sm text-slate-400" data-testid="nps-proyecto-empty">Sin datos.</p>
                ) : (
                  <Table className="[&_tbody_td]:py-2">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Proyecto</TableHead>
                        <TableHead className="text-right">NPS</TableHead>
                        <TableHead className="text-right">Muestra</TableHead>
                        <TableHead className="text-right">Clientes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.por_proyecto.map((p) => (
                        <TableRow
                          key={p.codigo_proyecto}
                          data-testid={`nps-proyecto-row-${p.codigo_proyecto}`}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => navigate(`/admin/experiencia/clientes?proyecto=${encodeURIComponent(p.codigo_proyecto)}&proyecto_nombre=${encodeURIComponent(p.nombre || p.codigo_proyecto)}`)}
                        >
                          <TableCell className="font-medium text-slate-800">{p.nombre}</TableCell>
                          <TableCell className="text-right">
                            <span className="inline-flex items-center gap-1.5 justify-end">
                              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: npsColor(p.nps) }} />
                              <b style={{ color: npsColor(p.nps) }}>{p.nps != null ? `${p.nps}%` : '—'}</b>
                            </span>
                          </TableCell>
                          <TableCell className="text-right text-slate-600">{p.muestra}</TableCell>
                          <TableCell className="text-right text-slate-600">{p.clientes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Clientes críticos */}
            <Card className="border-slate-200">
              <CardContent className="p-5 lg:p-6">
                <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Clientes críticos</h2>
                {data.clientes_criticos.length === 0 ? (
                  <p className="text-sm text-slate-400" data-testid="criticos-empty">No hay clientes críticos en esta segmentación.</p>
                ) : (
                  <div className="divide-y divide-slate-100" data-testid="criticos-list">
                    {data.clientes_criticos.map((c) => (
                      <button
                        key={c.cliente_id}
                        onClick={() => navigate(`/admin/experiencia/clientes/${c.cliente_id}`)}
                        className="w-full flex items-center justify-between py-2.5 text-left hover:bg-slate-50 px-2 rounded transition-colors"
                        data-testid={`critico-${c.cliente_id}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{c.nombre}</p>
                          <p className="text-xs text-slate-500">{c.ocupacion || '—'}</p>
                        </div>
                        <Badge className="bg-red-100 text-red-800 border-0 font-normal shrink-0">NPS {c.nps}</Badge>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default CXAnalyticsPage;
